import Card from '@mui/material/Card';
import { alpha, styled } from '@mui/material/styles';
import React from 'react';
import createConfigurableGridViewModel, {
  ViewModelType
} from '../../Components/ConfigurableGrid/configurable-grid-view-model';
import ConfigurableGridView from '../../Components/ConfigurableGrid/ConfigurableGridView';
import ProcessTab from './tabs/ProcessTab';
import TabPanel from './tabs/TabPanel';
import TabsContainer from './tabs/TabsContainer';
import TabTitle from './tabs/TabTitle';
import MainProcess from './tabs/variants/MainProcess';
import ProcessGate from './tabs/variants/ProcessGate';
import ProcessPhase from './tabs/variants/ProcessPhase';

const PREFIX = 'ProcessHierarchy';

const classes = {
  tabPanelContainer: `${PREFIX}-tabPanelContainer`,
  tabPanelContainerLast: `${PREFIX}-tabPanelContainerLast`,
  gridItem: `${PREFIX}-gridItem`,
  phaseTabRoot: `${PREFIX}-phaseTabRoot`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.tabPanelContainer}`]: {
    background: theme.palette.info.main,
    flexGrow: 1,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'visible',
    maxHeight: '182px'
  },
  [`& .${classes.tabPanelContainerLast}`]: {
    background: theme.palette.primary.light
  },

  [`& .${classes.gridItem}`]: {
    borderRight: `1px solid ${alpha(theme.palette.grey[900], 0.62)}`,
    borderBottom: `1px solid ${alpha(theme.palette.grey[900], 0.62)}`
  },
  [`& .${classes.phaseTabRoot}`]: {
    maxWidth: '100%',
    width: '100%',
    minWidth: '72px'
  }
}));

type Tab = {
  title: string;
  meaId?: string;
  type: string;
  children?: any;
  viewDefinitionId?: string;
};

type Tabs = Array<Tab>;

interface ProcessHierarchyProps {
  tabs: Tabs;
  viewDefinitionId?: string;
  firstLevel?: Boolean;
  onModalOpen: (meaId?: string, viewDefinitionId?: string) => void;
}

// eslint-disable-next-line no-shadow
const ProcessHierarchy = ({
  tabs,
  viewDefinitionId,
  firstLevel,
  onModalOpen
}: ProcessHierarchyProps) => {
  const [value, setValue] = React.useState<number | false>(false);
  const handleChange = (event: React.ChangeEvent<{}> | null, newValue: number | false) => {
    if (event) event.stopPropagation();
    if (value === newValue) {
      setValue(false);
    } else {
      setValue(newValue);
    }
  };

  const MainProcessTab = React.forwardRef((props: any, ref: any) => (
    <MainProcess {...props} innerRef={ref} />
  ));
  const ProcessPhaseTab = React.forwardRef((props: any, ref: any) => (
    <ProcessPhase {...props} innerRef={ref} />
  ));
  const ProcessGateTab = React.forwardRef((props: any, ref: any) => (
    <ProcessGate {...props} innerRef={ref} />
  ));

  const tabCount = tabs.length;

  let phaseNumber = 0;
  // eslint-disable-next-line no-shadow
  const getTab = (tab: Tab, index: number) => {
    const isLast =
      index + 1 === tabCount ||
      (tab.type === 'mainprocess' && tabs[index + 1].type !== 'mainprocess');

    if (tab.type === 'processgate') {
      return (
        <ProcessTab
          isSelected={false}
          key={`gate-${index}`}
          label={tab.title}
          component={ProcessGateTab}
        />
      );
    }

    if (tab.type === 'processphase') {
      phaseNumber += 1;

      return (
        <ProcessTab
          onInfoIconClick={() => onModalOpen(tab.meaId, tab.viewDefinitionId || viewDefinitionId)}
          key={`phase-${index}`}
          label={tab.title}
          number={phaseNumber}
          last={isLast}
          isSelected={value === index}
          firstLevel={!!firstLevel}
          component={ProcessPhaseTab}
          classes={{
            root: classes.phaseTabRoot
          }}
        />
      );
    }

    return [
      <ProcessTab
        onInfoIconClick={() => onModalOpen(tab.meaId, tab.viewDefinitionId || viewDefinitionId)}
        key={`path-${index}`}
        label={tab.title}
        firstLevel={!!firstLevel}
        isSelected={value === index}
        component={MainProcessTab}
        last={isLast}
      />
    ];
  };

  const getTabPanel = (tab: Tab, index: number) => {
    const viewModel: ViewModelType = createConfigurableGridViewModel({
      id: tab.meaId,
      viewDefinitionId: tab.viewDefinitionId || viewDefinitionId
    });

    const hasKids = tab.children?.length > 0;
    return (
      <>
        <Card className={hasKids ? classes.tabPanelContainer : classes.tabPanelContainerLast}>
          <TabPanel key={`tab-panel-${index}`} value={value} index={index}>
            {hasKids ? (
              <>
                <TabTitle title={tab.title} />
                <ProcessHierarchy
                  onModalOpen={onModalOpen}
                  tabs={tab.children}
                  viewDefinitionId={viewDefinitionId}
                />
              </>
            ) : (
              <ConfigurableGridView viewModel={viewModel} />
            )}
          </TabPanel>
        </Card>
      </>
    );
  };

  const selectedTab = value !== false && tabs[value];

  const hasKids = selectedTab && selectedTab.children?.length > 0;
  return (
    <Root>
      <TabsContainer
        value={value}
        onChange={handleChange}
        aria-label="E2E-phase-gate"
        first={firstLevel}
        last={!hasKids}
      >
        {tabs.map(getTab)}
      </TabsContainer>
      {tabs.map(getTabPanel)}
    </Root>
  );
};

export default ProcessHierarchy;
